import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReviewBot } from '../../services/providerReviewService';
import BotDetailPanel from './BotDetailPanel';
import { statusKind, isDestructive, actionKey, REJECTION_REASONS } from './botStatus';

// Base URL of the customer-facing provider UI (baked per env by the pipeline).
const PROVIDER_UI_URL = (process.env.REACT_APP_PROVIDER_UI_URL || '').replace(/\/$/, '');

interface Props {
  bots: ReviewBot[];
  /** Lifecycle targets to offer per current status. */
  actionsFor: (status: string) => string[];
  busy: string | null;
  onAction: (botId: string, to: string, note?: string, reasons?: string[]) => void;
  onVerify?: (providerId: string) => void;
  /** Approve/reject a live bot's pending instruction edit (edit re-review). */
  onEditReview?: (botId: string, approve: boolean, note?: string, reasons?: string[]) => void;
}

/**
 * Shared bot table for the Review and All-Bots pages: expandable rows opening
 * the comprehensive detail panel, localized colored status pills, and
 * destructive actions (reject / suspend) gated behind an inline note+confirm.
 */
const BotTable: React.FC<Props> = ({ bots, actionsFor, busy, onAction, onVerify, onEditReview }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  // Pending destructive action: which (botId, to) is awaiting confirm.
  // to === '__rejectEdit' targets the pending-edit re-review flow.
  const [confirming, setConfirming] = useState<{ botId: string; to: string } | null>(null);
  const [note, setNote] = useState('');
  const [reasons, setReasons] = useState<string[]>([]);

  const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '—');

  /**
   * Clicking anywhere on a row opens its detail panel — the caret alone was a
   * tiny target in the corner of a very wide table, and the rename field (the
   * only place a platform-managed bot can be renamed) lives behind it.
   *
   * The row is full of real controls (lifecycle buttons, the public-page link,
   * the reject note + reason checkboxes), so a click that lands on any
   * interactive element is left alone. Text selection is likewise not a click.
   */
  const rowToggle = (e: React.MouseEvent<HTMLTableRowElement>, botId: string) => {
    const el = e.target as HTMLElement;
    if (el.closest('button, a, input, label, select, textarea')) return;
    if (window.getSelection()?.toString()) return;
    setExpanded(expanded === botId ? null : botId);
  };

  // Rejections (reject-to-Draft or edit-reject) REQUIRE ≥1 structured reason —
  // the codes are stored in DDB and shown, localized, to the provider.
  const needsReasons = (to: string) => to === 'Draft' || to === '__rejectEdit';

  const startAction = (b: ReviewBot, to: string) => {
    if (to === '__rejectEdit' || isDestructive(b.status, to)) {
      setConfirming({ botId: b.botId, to });
      setNote('');
      setReasons([]);
    } else {
      onAction(b.botId, to);
    }
  };

  const toggleReason = (code: string) => {
    setReasons((r) => (r.includes(code) ? r.filter((x) => x !== code) : [...r, code]));
  };

  // 'other' carries no meaning on its own ("その他（備考参照）") — the server
  // 400s (note_required_for_other) without a note, so block it client-side too.
  const otherNeedsNote = (to: string) =>
    needsReasons(to) && reasons.includes('other') && !note.trim();

  const confirmAction = () => {
    if (!confirming) return;
    if (needsReasons(confirming.to) && reasons.length === 0) return;
    if (otherNeedsNote(confirming.to)) return;
    if (confirming.to === '__rejectEdit') {
      onEditReview?.(confirming.botId, false, note.trim() || undefined, reasons);
    } else {
      onAction(confirming.botId, confirming.to, note.trim() || undefined, needsReasons(confirming.to) ? reasons : undefined);
    }
    setConfirming(null);
  };

  return (
    <table className="review-table">
      <thead>
        <tr>
          <th>{t('review.bot', 'Bot')}</th>
          <th>{t('review.status', 'Status')}</th>
          <th>{t('review.domain', 'Domain')}</th>
          <th>{t('review.entry', 'Entry')}</th>
          <th>{t('review.updated', 'Updated')}</th>
          <th>{t('review.actions', 'Actions')}</th>
        </tr>
      </thead>
      <tbody>
        {bots.map((b) => (
          <React.Fragment key={b.botId}>
            <tr
              className={'review-row' + (expanded === b.botId ? ' row-expanded' : '')}
              onClick={(e) => rowToggle(e, b.botId)}
            >
              <td>
                <button
                  className="review-expand"
                  aria-expanded={expanded === b.botId}
                  aria-controls={`bot-detail-${b.botId}`}
                  title={t('review.detail.toggle', 'Show details')}
                  onClick={() => setExpanded(expanded === b.botId ? null : b.botId)}
                >
                  <span aria-hidden="true">{expanded === b.botId ? '▾' : '▸'}</span>
                  {expanded === b.botId
                    ? t('review.detail.hide', 'Hide details')
                    : t('review.detail.toggle', 'Show details')}
                </button>
                <b>{b.displayName}</b>
                <div className="review-id">
                  {b.botId.slice(0, 8)}
                  {b.slug && PROVIDER_UI_URL && (
                    <>
                      {' · '}
                      <a href={`${PROVIDER_UI_URL}/experts.html?slug=${encodeURIComponent(b.slug)}`} target="_blank" rel="noreferrer">
                        {t('review.publicPage', 'Public page')}{b.status !== 'Published' ? t('review.publicPageUnpublished', ' (not live)') : ''}
                      </a>
                    </>
                  )}
                </div>
              </td>
              <td>
                <span className={'status-pill ' + statusKind(b.status)}>{t(`status.${b.status}`, b.status)}</span>
                {b.editReview && (
                  <span className="status-pill submitted" style={{ marginLeft: 4 }}>
                    {t('review.editReviewBadge', 'Edit review')}
                  </span>
                )}
                {b.providerVerified === false && (
                  <span className="status-pill rejected" style={{ marginLeft: 4 }} title={t('review.verifyHint', 'Mark this provider identity-verified (required before Published)') as string}>
                    {t('review.unverifiedBadge', 'Provider unverified')}
                  </span>
                )}
                <div className="review-id" style={{ marginTop: 2 }}>{t(`nextStepAdmin.${b.status}`, '')}</div>
              </td>
              <td>{t(`domain.${b.disclaimerCategory}`, b.disclaimerCategory)}</td>
              <td className="review-date">{b.entryChannel || '—'}</td>
              <td className="review-date">{fmtDate(b.updatedAt || b.submittedAt || b.createdAt)}</td>
              <td className="review-actions">
                {confirming?.botId === b.botId ? (
                  <span className="confirm-inline" style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {needsReasons(confirming.to) && (
                      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 420, textAlign: 'left' }}>
                        {REJECTION_REASONS.map((code) => (
                          <label key={code} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
                            <input type="checkbox" checked={reasons.includes(code)} onChange={() => toggleReason(code)} />
                            {t(`reasons.${code}`, code)}
                          </label>
                        ))}
                      </span>
                    )}
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <input
                        autoFocus
                        value={note}
                        placeholder={t('review.notePlaceholder', 'Note (optional, shown to provider)') as string}
                        onChange={(e) => setNote(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) confirmAction(); }}
                      />
                      <button
                        className="danger"
                        disabled={busy === b.botId + confirming.to || (needsReasons(confirming.to) && reasons.length === 0) || otherNeedsNote(confirming.to)}
                        title={
                          needsReasons(confirming.to) && reasons.length === 0
                            ? (t('review.reasonsRequired', 'Select at least one reason') as string)
                            : otherNeedsNote(confirming.to)
                              ? (t('review.noteRequiredOther', 'Add a note when “Other” is selected') as string)
                              : undefined
                        }
                        onClick={confirmAction}
                      >
                        {confirming.to === '__rejectEdit'
                          ? t('review.detail.rejectEdit', 'Reject edit')
                          : t(`review.${actionKey(b.status, confirming.to)}`, confirming.to)}
                      </button>
                      <button className="secondary" onClick={() => setConfirming(null)}>
                        {t('review.cancel', 'Cancel')}
                      </button>
                    </span>
                  </span>
                ) : (
                  <>
                    {b.editReview && onEditReview && (
                      <>
                        <button
                          disabled={busy === b.botId + 'editApprove'}
                          onClick={() => onEditReview(b.botId, true)}
                          title={t('review.approveEditHint', 'Promote the pending edit to live') as string}
                        >
                          {t('review.detail.approveEdit', 'Approve edit')}
                        </button>
                        <button
                          className="secondary danger-text"
                          disabled={busy === b.botId + '__rejectEdit'}
                          onClick={() => startAction(b, '__rejectEdit')}
                        >
                          {t('review.detail.rejectEdit', 'Reject edit')}
                        </button>
                      </>
                    )}
                    {actionsFor(b.status).map((to) => (
                      <button
                        key={to}
                        className={isDestructive(b.status, to) ? 'secondary danger-text' : undefined}
                        disabled={busy === b.botId + to}
                        onClick={() => startAction(b, to)}
                      >
                        {actionKey(b.status, to) === 'action.advance'
                          ? `${t(`status.${to}`, to)} →`
                          : t(`review.${actionKey(b.status, to)}`, to)}
                      </button>
                    ))}
                    {onVerify && b.providerVerified === false && (
                      <button
                        className="secondary"
                        disabled={busy === 'v' + b.providerId}
                        onClick={() => onVerify(b.providerId)}
                        title={t('review.verifyHint', 'Mark this provider identity-verified (required before Published)')}
                      >
                        {t('review.verifyProvider', 'Verify provider')}
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
            {expanded === b.botId && (
              <tr className="review-detail-row">
                <td colSpan={6} id={`bot-detail-${b.botId}`}><BotDetailPanel botId={b.botId} /></td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default BotTable;
